import api from './api';
import { buildVentaSearchQueryParams } from '../utils/ventaSearchQueryParams';

export interface DevolucionVentaItem {
  nroFormulario: number;
  nroTipoFormulario?: string;
  nroSuc?: number;
  nroComprobante?: number;
  letra?: string;
  fechaEmision?: string;
  nroCliente?: number;
  nombre?: string;
  importeTotal?: number;
  descripcion?: string;
  formaDePago?: string;
  usuario?: string;
  asentado?: string;
}

export interface DevolucionesSearchParams {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  nroCliente?: string;
  nroSuc?: number;
  asentado?: string;
}

export interface DevolucionesPagedResponse {
  items: DevolucionVentaItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface DevolucionMedioReintegroPayload {
  mcp: string;
  importe: number;
  detalle?: string;
}

export interface DevolucionVentaCreatePayload {
  formulario: {
    idCompania: string;
    nroCliente: number;
    nroSuc: number;
    importeTotal: number;
    descripcion?: string;
    fechaEmision?: string;
    nroFormularioFactura?: number;
  };
  grupoMCP: number;
  medioReintegro: DevolucionMedioReintegroPayload;
}

const mapItem = (raw: Record<string, unknown>): DevolucionVentaItem => ({
  nroFormulario: Number(raw.nroFormulario ?? raw.NroFormulario ?? 0),
  nroTipoFormulario: String(raw.nroTipoFormulario ?? raw.NroTipoFormulario ?? '60'),
  nroSuc: Number(raw.nroSuc ?? raw.NroSuc ?? 0) || undefined,
  nroComprobante: Number(raw.nroComprobante ?? raw.NroComprobante ?? 0) || undefined,
  letra: (raw.letra ?? raw.Letra) as string | undefined,
  fechaEmision: (raw.fechaEmision ?? raw.FechaEmision) as string | undefined,
  nroCliente: Number(raw.nroCliente ?? raw.NroCliente ?? 0) || undefined,
  nombre: (raw.nombre ?? raw.Nombre) as string | undefined,
  importeTotal: Number(raw.importeTotal ?? raw.ImporteTotal ?? 0),
  descripcion: (raw.descripcion ?? raw.Descripcion) as string | undefined,
  formaDePago: (raw.formaDePago ?? raw.FormaDePago) as string | undefined,
  usuario: (raw.usuario ?? raw.Usuario) as string | undefined,
  asentado: (raw.asentado ?? raw.Asentado) as string | undefined,
});

const devolucionVentaService = {
  list: async (params: DevolucionesSearchParams = {}): Promise<DevolucionesPagedResponse> => {
    const queryParams = buildVentaSearchQueryParams({
      pageNumber: params.pageNumber ?? 1,
      pageSize: params.pageSize ?? 10,
      searchTerm: params.searchTerm,
      fechaDesde: params.fechaDesde,
      fechaHasta: params.fechaHasta,
      nroCliente: params.nroCliente,
      nroSuc: params.nroSuc,
      asentado: params.asentado,
    });
    const response = await api.get('/api/DevolucionesVenta', { params: queryParams });
    const data = response.data ?? {};
    const rawItems = (data.items ?? data.Items ?? []) as Record<string, unknown>[];
    return {
      items: Array.isArray(rawItems) ? rawItems.map(mapItem) : [],
      totalCount: Number(data.totalCount ?? data.TotalCount ?? 0),
      pageNumber: Number(data.pageNumber ?? data.PageNumber ?? 1),
      pageSize: Number(data.pageSize ?? data.PageSize ?? 10),
      totalPages: Number(data.totalPages ?? data.TotalPages ?? 0),
    };
  },

  get: async (id: number): Promise<DevolucionVentaItem> => {
    const response = await api.get(`/api/DevolucionesVenta/${id}`);
    return mapItem(response.data ?? {});
  },

  create: async (payload: DevolucionVentaCreatePayload): Promise<DevolucionVentaItem> => {
    const response = await api.post('/api/DevolucionesVenta', payload);
    return mapItem(response.data ?? {});
  },

  getPdf: async (id: number): Promise<Blob> => {
    const response = await api.get(`/api/DevolucionesVenta/${id}/pdf`, { responseType: 'blob' });
    return response.data;
  },

  puedeAnular: async (id: number): Promise<{ puedeAnular: boolean; motivos: string[] }> => {
    const response = await api.get<{ puedeAnular?: boolean; PuedeAnular?: boolean; motivos?: string[] | null; Motivos?: string[] | null }>(
      `/api/DevolucionesVenta/${id}/puede-anular`
    );
    const data = response.data ?? {};
    return {
      puedeAnular: Boolean(data.puedeAnular ?? data.PuedeAnular),
      motivos: (data.motivos ?? data.Motivos ?? []).filter((m): m is string => Boolean(m && String(m).trim())),
    };
  },

  anular: async (id: number, motivo?: string): Promise<void> => {
    await api.post(`/api/DevolucionesVenta/${id}/anular`, { motivo: motivo ?? null });
  },
};

export default devolucionVentaService;
